import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";

import AppButton from "../../components/buyer/AppButton";
import AppLoader from "../../components/seller/AppLoader";
import AppToast from "../../components/seller/AppToast";

import {
  fetchOutOfRange,
  acceptCharges,
  rejectCharges,
} from "../../features/buyer/buyerSlice";

import { capitalizeFirst } from "../../utils/stringUtils";
import { timeAgo } from "../../utils/timeAgo";
import { formatPrice } from "../../utils/price";

const PAGE_SIZE = 10;

const STATUS_COLORS = {
  pending: { bg: "#fff3cd", color: "#856404" },
  accepted: { bg: "#d4edda", color: "#155724" },
  rejected: { bg: "#f8d7da", color: "#721c24" },
  default: { bg: "#f8f9fa", color: "#374151" },
};

const RangeRequests = () => {
  const dispatch = useDispatch();

  const { data = [], loading } = useSelector((state) => state.buyer.outOfRange);

  const { loading: acceptLoading, success: acceptSuccess } = useSelector(
    (state) => state.buyer.acceptCharges
  );

  const { loading: rejectLoading, success: rejectSuccess } = useSelector(
    (state) => state.buyer.rejectCharges
  );

  const [page, setPage] = useState(1);
  const [toast, setToast] = useState({
    visible: false,
    type: "info",
    message: "",
  });

  const [actionModal, setActionModal] = useState({
    open: false,
    type: null, // accept | reject
    row: null,
  });

  /* ---------------- FETCH LIST ---------------- */
  useEffect(() => {
    dispatch(fetchOutOfRange());
  }, [dispatch]);

  /* ---------------- REFRESH AFTER ACTION ---------------- */
  useEffect(() => {
    if (acceptSuccess || rejectSuccess) {
      dispatch(fetchOutOfRange());
      setActionModal({ open: false, type: null, row: null });

      setToast({
        visible: true,
        type: "success",
        message: acceptSuccess
          ? "Charges accepted successfully"
          : "Charges rejected successfully",
      });
    }
  }, [acceptSuccess, rejectSuccess, dispatch]);

  /* ---------------- PAGINATION ---------------- */
  const totalPages = Math.ceil(data.length / PAGE_SIZE);
  const paginatedData = useMemo(() => {
    return data.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [data, page]);

  const fields = [
    "#",
    "Order ID",
    "Product",
    "Seller",
    "Distance (km)",
    "Allowed Radius (km)",
    "Extra Charge (AUD)",
    "Status",
    "Buyer Acceptance",
    "Created At",
    "Seller Responded",
    "Buyer Responded",
    "Actions",
  ];

  const handleConfirmAction = async () => {
    try {
      if (actionModal.type === "accept") {
        await dispatch(acceptCharges(actionModal.row.id)).unwrap();
      } else {
        await dispatch(rejectCharges(actionModal.row.id)).unwrap();
      }
    } catch (err) {
      setToast({
        visible: true,
        type: "error",
        message: err || "Something went wrong",
      });
    }
  };

  const safe = (value, fallback = "-") => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value === "null"
    ) {
      return fallback;
    }
    return value;
  };

  if (loading) {
    return <AppLoader />;
  }

  return (
    <>
      <Card
        sx={{
          p: { xs: 3, sm: 5 },
          mb: 8,
          borderRadius: 3,
          background: "#f9fff9",
          border: "1px solid rgba(0,0,0,0.05)",
          boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
        }}
      >
        <Typography variant="h5" fontWeight={600} mb={3}>
          Out of Range Requests
        </Typography>

        <Box sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 1500 }}>
            <TableHead>
              <TableRow>
                {fields.map((h) => (
                  <TableCell key={h} align="center" sx={{ fontWeight: 600 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={fields.length}
                    align="center"
                    sx={{ py: 6 }}
                  >
                    No requests found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, index) => {
                  const statusColor =
                    STATUS_COLORS[row.status] || STATUS_COLORS.default;

                  const canAct = row.buyer_acceptance === "pending";

                  return (
                    <TableRow key={row.id}>
                      <TableCell align="center">
                        {(page - 1) * PAGE_SIZE + index + 1}
                      </TableCell>
                      <TableCell align="center">{safe(row.order_id)}</TableCell>
                      <TableCell align="center">
                        {row.order.product.name}
                      </TableCell>
                      <TableCell align="center">
                        {safe(row.seller?.name)}
                      </TableCell>
                      <TableCell align="center">
                        {safe(row.distance_km)}
                      </TableCell>
                      <TableCell align="center">
                        {safe(row.allowed_radius_km)}
                      </TableCell>
                      <TableCell align="center">
                        {/* A$ {safe(formatPrice(row.extra_delivery_charge), "  -")} */}
                        {formatPrice(row.extra_delivery_charge)}
                      </TableCell>

                      <TableCell align="center">
                        {row.status ? (
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              fontWeight: 600,
                              display: "inline-block",
                              background:
                                STATUS_COLORS[row.status]?.bg ||
                                STATUS_COLORS.default.bg,
                              color:
                                STATUS_COLORS[row.status]?.color ||
                                STATUS_COLORS.default.color,
                            }}
                          >
                            {capitalizeFirst(row.status)}
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      <TableCell align="center">
                        {safe(capitalizeFirst(row.buyer_acceptance))}
                      </TableCell>

                      <TableCell align="center">
                        {safe(timeAgo(row.created_at))}
                      </TableCell>
                      <TableCell align="center">
                         {safe(timeAgo(row.seller_responded_at))}
                      </TableCell>
                      <TableCell align="center">
                        {safe(timeAgo(row.buyer_responded_at))}
                      </TableCell>

                      <TableCell align="center">
                        {canAct ? (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              justifyContent: "center",
                            }}
                          >
                            <AppButton
                              variant="outlined"
                              onClick={() =>
                                setActionModal({
                                  open: true,
                                  type: "accept",
                                  row,
                                })
                              }
                            >
                              Accept
                            </AppButton>
                            <AppButton
                              variant="outlined"
                              style={{
                                borderColor: "#d32f2f",
                                color: "#d32f2f",
                              }}
                              onClick={() =>
                                setActionModal({
                                  open: true,
                                  type: "reject",
                                  row,
                                })
                              }
                            >
                              Reject
                            </AppButton>
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>

        {totalPages > 1 && (
          <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          </Box>
        )}
      </Card>

      {/* ================= ACTION MODAL ================= */}
      <Dialog
        open={actionModal.open}
        onClose={() => setActionModal({ open: false })}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography>
            {actionModal.type === "accept"
              ? "Accept Extra Charges"
              : "Reject Extra Charges"}
          </Typography>
          <IconButton onClick={() => setActionModal({ open: false })}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          Are you sure you want to <b>{actionModal.type}</b> the extra delivery
          charges?
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setActionModal({ open: false })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={actionModal.type === "accept" ? "success" : "error"}
            onClick={handleConfirmAction}
            disabled={acceptLoading || rejectLoading}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <AppToast
        visible={toast.visible}
        setVisible={(v) => setToast((p) => ({ ...p, visible: v }))}
        type={toast.type}
        message={toast.message}
      />
    </>
  );
};

export default RangeRequests;
